/**
 * reportsSuite.service — R5 Reports Suite (2026-05-17).
 *
 * Thin RPC wrappers for the five reports + Events page bulk-stats/name-resolver
 * helpers that backfill R3+R4 deferrals. All RPCs are SECURITY DEFINER on the
 * DB side; the verified-members RPC additionally enforces role server-side
 * per R5-D5 audit amendment.
 *
 * Naming: this file is "reportsSuite" not "reports" because reports.service.ts
 * already exists for the unrelated report_schedules feature.
 *
 * Shape contracts intentionally mirror the RPC return shapes 1:1 so the
 * mapper functions in each report page can pass the rows straight to
 * ExportButton's mapRows without a translation layer.
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";
import { toRpcError } from "../utils/rpcError";

// ---------------- Filter shapes ----------------

export interface ReportEventsFilter {
  orgIds?: UUID[];
  status?: string;
  type?: string;
  state?: string;
  dateFrom?: string; // YYYY-MM-DD
  dateTo?: string; // YYYY-MM-DD
  search?: string;
  sort?: "start_time" | "created_at" | "title" | "status" | "type";
  sortDirection?: "asc" | "desc";
  page: number;
  limit: number;
}

export interface VerifiedMembersFilter {
  orgIds?: UUID[];
  state?: string;
  eventType?: string;
  dateFrom?: string;
  dateTo?: string;
  page: number;
  limit: number;
}

// ---------------- DTO shapes (mirror RPC RETURNS TABLE) ----------------

export interface EventReportRow {
  id: UUID;
  title: string;
  type: string;
  status: string;
  start_time: string | null;
  end_time: string | null;
  location_name: string | null;
  location_state: string | null;
  location_city: string | null;
  location_address: unknown;
  state: string | null;
  attendance_count: number | null;
  sales_position_id: UUID | null;
  territory_manager_position_id: UUID | null;
  regional_manager_position_id: UUID | null;
  sales_director_position_id: UUID | null;
  cancellation_reason: string | null;
  cancellation_notes: string | null;
  created_at: string;
  updated_at: string;
  cta_count: number;
  total_count: number;
}

export interface EventCloseRateStatsRow {
  event_id: UUID;
  attendance_count: number | null;
  verified_member_count: number;
  close_rate: number | null;
  computed_at: string;
}

export interface PositionNameRow {
  position_id: UUID;
  title: string;
  position_type: string;
  assigned_profile_id: UUID | null;
  assigned_name: string | null;
  assigned_email: string | null;
  profile_email: string | null;
  effective_email: string | null;
  is_active: boolean;
}

export interface VerifiedMemberRow {
  // Phase 1.1 Ship 4b — v2 introduces source_type. 'voucher' rows
  // keep voucher_id; 'attendee' rows have voucher_id NULL +
  // is_zuora_confirmed populated.
  source_type?: "voucher" | "attendee";
  voucher_id: UUID | null;
  event_id: UUID;
  event_title: string | null;
  event_date: string | null;
  contact_id: UUID;
  contact_first_name: string | null;
  contact_last_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  member_number: string | null;
  credited_rep_position_id: UUID | null;
  voucher_status: string | null;
  voucher_created_at: string;
  is_zuora_confirmed?: boolean | null;
  total_count: number;
}

// ---------------- RPC callers ----------------

/**
 * R5-D4: filter-typed events fetch via RPC. Replaces R4's unbounded
 * event_organizations prefetch in getEvents() (post-ship audit finding #2).
 */
export async function getEventsForOrganizations(
  filter: ReportEventsFilter,
): Promise<{ rows: EventReportRow[]; total: number }> {
  const { data, error } = await supabase.rpc("get_events_for_organizations", {
    p_org_ids: filter.orgIds && filter.orgIds.length > 0 ? filter.orgIds : null,
    p_status: filter.status || null,
    p_type: filter.type || null,
    p_state: filter.state || null,
    p_date_from: filter.dateFrom || null,
    p_date_to: filter.dateTo || null,
    p_search: filter.search || null,
    p_sort: filter.sort || "start_time",
    p_sort_direction: filter.sortDirection || "desc",
    p_page: filter.page,
    p_limit: filter.limit,
  });
  if (error) {
    throw error;
  }
  const rows = (data ?? []) as EventReportRow[];
  const total = rows.length > 0 ? Number(rows[0].total_count) : 0;
  return { rows, total };
}

/**
 * R5-D2 (audit-amended): bulk close-rate stats. Capped at 5000 IDs per call.
 * Caller should chunk if a report has >5000 events in a single page.
 */
export async function getEventCloseRateStatsBulk(
  eventIds: UUID[],
): Promise<EventCloseRateStatsRow[]> {
  if (eventIds.length === 0) {
    return [];
  }
  const { data, error } = await supabase.rpc(
    "get_event_close_rate_stats_bulk",
    { p_event_ids: eventIds },
  );
  if (error) {
    throw toRpcError("Could not load event close-rate stats", error);
  }
  return (data ?? []) as EventCloseRateStatsRow[];
}

/**
 * R5-D3 (audit-amended): bulk name + effective_email resolver. Used by
 * reports (display labels) and by R6's reminder edge function (effective
 * email). Returns full shape so neither caller needs a second query.
 */
export async function resolvePositionNamesBulk(
  positionIds: UUID[],
): Promise<PositionNameRow[]> {
  const dedupe = Array.from(new Set(positionIds.filter(Boolean)));
  if (dedupe.length === 0) {
    return [];
  }
  const { data, error } = await supabase.rpc("resolve_position_names_bulk", {
    p_position_ids: dedupe,
  });
  if (error) {
    throw toRpcError("Could not load rep names", error);
  }
  return (data ?? []) as PositionNameRow[];
}

/**
 * R5-D5 (audit-amended): server-side role-gated verified-members PII fetch.
 * Frontend route gating is necessary but not sufficient — RPC raises if
 * caller's role isn't superadmin/admin/manager.
 */
export async function getVerifiedMembersReport(
  filter: VerifiedMembersFilter,
): Promise<{ rows: VerifiedMemberRow[]; total: number }> {
  // Phase 1.1 Ship 4b — switched to v2 which UNIONs voucher + attendee
  // rows. Legacy function still exists but isn't called.
  const { data, error } = await supabase.rpc("get_verified_members_report_v2", {
    p_org_ids: filter.orgIds && filter.orgIds.length > 0 ? filter.orgIds : null,
    p_date_from: filter.dateFrom || null,
    p_date_to: filter.dateTo || null,
    p_state: filter.state || null,
    p_event_type: filter.eventType || null,
    p_page: filter.page,
    p_limit: filter.limit,
  });
  if (error) {
    throw error;
  }
  const rows = (data ?? []) as VerifiedMemberRow[];
  const total = rows.length > 0 ? Number(rows[0].total_count) : 0;
  return { rows, total };
}

/**
 * R5-D5: log a bulk PII export action. Called by the verified-members
 * export button after a successful export completes.
 */
export async function logPiiExport(
  exportType: string,
  rowCount: number,
  filterSummary: unknown,
): Promise<UUID> {
  const { data, error } = await supabase.rpc("log_pii_export", {
    p_export_type: exportType,
    p_row_count: rowCount,
    p_filter_summary: filterSummary,
  });
  if (error) {
    throw error;
  }
  return data as UUID;
}

// ---------------- R5 wave 2: aggregation RPCs ----------------

export interface SalesActivityByTypeRow {
  event_type: string;
  event_count: number;
  total_attendance: number;
  total_verified_members: number;
  total_vouchers: number;
  total_count: number;
}

export interface SalesByRepRow {
  credited_rep_position_id: UUID | null; // null = Unattributed bucket
  // Legacy voucher-based metrics (shown during cutover window)
  voucher_count: number;
  verified_member_count: number;
  event_count: number;
  // Phase 1.1 Ship 4b — new attendee-based metrics
  attributed_contact_count?: number;
  verified_member_count_new?: number;
  pending_zuora_count?: number;
  attendee_event_count?: number;
  attendee_row_count?: number;
  total_count: number;
}

export interface AggregationFilter {
  orgIds?: UUID[];
  state?: string;
  dateFrom?: string;
  dateTo?: string;
  page: number;
  limit: number;
}

/**
 * R5 wave 2: events grouped by type. Operator-friendly KPI rollup.
 */
export async function getSalesActivityByType(
  filter: AggregationFilter,
): Promise<{ rows: SalesActivityByTypeRow[]; total: number }> {
  const { data, error } = await supabase.rpc("get_sales_activity_by_type", {
    p_org_ids: filter.orgIds && filter.orgIds.length > 0 ? filter.orgIds : null,
    p_date_from: filter.dateFrom || null,
    p_date_to: filter.dateTo || null,
    p_state: filter.state || null,
    p_page: filter.page,
    p_limit: filter.limit,
  });
  if (error) {
    throw error;
  }
  const rows = (data ?? []) as SalesActivityByTypeRow[];
  const total = rows.length > 0 ? Number(rows[0].total_count) : 0;
  return { rows, total };
}

/**
 * R5 wave 2 (R5-D6 amended): vouchers grouped by credited CTA rep.
 * Includes "Unattributed" bucket (credited_rep_position_id IS NULL) unless
 * the caller opts out via includeUnattributed=false.
 */
export async function getSalesByRep(
  filter: AggregationFilter & { includeUnattributed?: boolean },
): Promise<{ rows: SalesByRepRow[]; total: number }> {
  // Phase 1.1 Ship 4b — switched to v2 which adds attendee-based
  // attribution columns. Legacy still exists but isn't called.
  const { data, error } = await supabase.rpc("get_sales_by_rep_v2", {
    p_org_ids: filter.orgIds && filter.orgIds.length > 0 ? filter.orgIds : null,
    p_date_from: filter.dateFrom || null,
    p_date_to: filter.dateTo || null,
    p_state: filter.state || null,
    p_include_unattributed: filter.includeUnattributed ?? true,
    p_page: filter.page,
    p_limit: filter.limit,
  });
  if (error) {
    throw error;
  }
  const rows = (data ?? []) as SalesByRepRow[];
  const total = rows.length > 0 ? Number(rows[0].total_count) : 0;
  return { rows, total };
}

// ---------------- Convenience helpers ----------------

/**
 * Build a Map<positionId, PositionNameRow> from a resolver result for O(1)
 * lookup during row mapping. Caller-side helper; doesn't hit the RPC.
 */
export function indexPositionsById(
  rows: PositionNameRow[],
): Map<UUID, PositionNameRow> {
  const idx = new Map<UUID, PositionNameRow>();
  for (const r of rows) {
    idx.set(r.position_id, r);
  }
  return idx;
}

/**
 * Build a Map<eventId, EventCloseRateStatsRow> from a bulk stats result.
 */
export function indexCloseRateStatsByEventId(
  rows: EventCloseRateStatsRow[],
): Map<UUID, EventCloseRateStatsRow> {
  const idx = new Map<UUID, EventCloseRateStatsRow>();
  for (const r of rows) {
    idx.set(r.event_id, r);
  }
  return idx;
}
